import XLSXStyle from "xlsx-js-style";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import moment from "moment";

// Formato Unico de Requerimiento de Pedido (complemento formatoRequerimientoCotizaciones).
// Es el Excel de un cliente (PED-FOR-001 v2), asi que se replica su maqueta y sus colores
// en lugar de la paleta de Biwo. El que se regresa al guardar la cotizacion agrega, a la
// derecha del detalle, las columnas de precio de uso interno. Vive sin React, antd ni axios
// para poder generarlo y leerlo en node sin levantar la app.

export const FORMATO_CONTABILIDAD = '_-"$"* #,##0.00_-;-"$"* #,##0.00_-;_-"$"* "-"??_-;_-@_-';
const FORMATO_FECHA = "dd/mm/yyyy";
// El cero no se pinta: las cantidades en blanco van en 0 (ver celdaNumero).
const FORMATO_ENTERO_SIN_CERO = "0;-0;;@";

// Lista desplegable de la celda Canal, igual que la del cliente.
export const CANALES_REQUERIMIENTO = ["Venta directa", "Subdistribuidor", "Institución"];
const CELDA_CANAL = "B5";

export const RENGLONES_DESTINO_MINIMOS = 3;
export const RENGLONES_PRODUCTO_MINIMOS = 6;

// Anchos del formato del cliente (A..F). Con precios se agregan F..I y la F deja de ser
// la columna angosta de relleno.
const ANCHOS_FORMATO = [28, 30, 20, 18, 20, 10];
const ANCHOS_CON_PRECIOS = [28, 30, 20, 18, 20, 17, 12, 24, 18];
const ANCHO_DESCRIPCION = 30;

const COLOR_BANDA = "DDEBF7";
const COLOR_ENCABEZADO = "1F4E78";
const COLOR_CANAL = "FFF2CC";
const COLOR_FECHA = "E2F0D9";
const COLOR_BORDE = "D9D9D9";

// ------------------------------------------------------------------ estilos

const borde = { style: "thin", color: { rgb: COLOR_BORDE } };
const bordes = { top: borde, bottom: borde, left: borde, right: borde };
const fuente = (extra = {}) => ({ name: "Calibri", sz: 11, ...extra });
const centrado = { horizontal: "center", vertical: "center" };

const estiloTitulo = { font: fuente({ bold: true, sz: 16 }), alignment: centrado };
const estiloBanda = {
    font: fuente({ bold: true }),
    fill: { fgColor: { rgb: COLOR_BANDA } },
    alignment: { vertical: "center" },
};
const estiloEtiqueta = { font: fuente({ bold: true }), alignment: { vertical: "center" } };
const estiloEncabezado = {
    font: fuente({ bold: true, sz: 12, color: { rgb: "FFFFFF" } }),
    fill: { fgColor: { rgb: COLOR_ENCABEZADO } },
    alignment: { vertical: "center" },
    border: bordes,
};

const estiloCelda = { font: fuente(), alignment: centrado, border: bordes };
const estiloCeldaLarga = { ...estiloCelda, alignment: { ...centrado, wrapText: true } };
const estiloCanal = { ...estiloCelda, fill: { fgColor: { rgb: COLOR_CANAL } } };
// Las fechas del cliente van en verde y sin borde, como en su formato.
const estiloFechaCliente = {
    font: fuente(),
    fill: { fgColor: { rgb: COLOR_FECHA } },
    alignment: centrado,
    numFmt: FORMATO_FECHA,
};
const estiloFecha = { ...estiloCelda, numFmt: FORMATO_FECHA };
const estiloIsbn = { ...estiloCelda, numFmt: "@" };
const estiloEntero = { ...estiloCelda, numFmt: FORMATO_ENTERO_SIN_CERO };
const estiloMoneda = { ...estiloCelda, numFmt: FORMATO_CONTABILIDAD };
const estiloPorcentaje = { ...estiloCelda, numFmt: "0%" };
const estiloTotalEtiqueta = {
    font: fuente({ bold: true }),
    alignment: { horizontal: "right", vertical: "center" },
};
const estiloTotal = {
    font: fuente({ bold: true }),
    fill: { fgColor: { rgb: COLOR_BANDA } },
    alignment: centrado,
    border: bordes,
    numFmt: FORMATO_CONTABILIDAD,
};

// ------------------------------------------------------------------ celdas

const vacio = (valor) => valor === undefined || valor === null || String(valor).trim() === "";

const celdaTexto = (valor, estilo) => ({
    v: vacio(valor) ? "" : String(valor),
    t: "s",
    s: estilo,
});

// OJO: una celda {v: ""} se escribe como TEXTO vacio, y una formula que la use
// (F - G*F, D*H) da #¡VALOR! en Excel. Por eso los numeros en blanco van en 0.
const celdaNumero = (valor, estilo) => {
    const numero = Number(valor);
    return {
        v: vacio(valor) || Number.isNaN(numero) ? 0 : numero,
        t: "n",
        s: estilo,
    };
};

const celdaFormula = (formula, valor, estilo) => ({
    f: formula,
    v: Number.isFinite(valor) ? valor : 0,
    t: "n",
    s: estilo,
});

// La edicion suele ser un año (2025): como numero para que Excel no la marque como
// "numero almacenado como texto".
const celdaEdicion = (valor) => {
    const texto = vacio(valor) ? "" : String(valor).trim();
    return /^\d{1,6}$/.test(texto) ? { v: Number(texto), t: "n", s: estiloCelda } : celdaTexto(texto, estiloCelda);
};

// Excel no recalcula al abrir el alto de un renglon con texto ajustado: se estima con el
// ancho de la columna (en caracteres) para que la direccion o la descripcion no se corten.
const altoTextoAjustado = (texto, anchoColumna) => {
    if (vacio(texto)) return null;
    const lineas = Math.ceil(String(texto).trim().length / anchoColumna);
    return lineas > 1 ? lineas * 15 : null;
};

// ------------------------------------------------------------------ fechas

const MS_DIA = 86400000;
const EPOCA_EXCEL = Date.UTC(1899, 11, 30);

const textoFechaValido = (anio, mes, dia) => {
    if (!(anio >= 1900 && anio <= 2999 && mes >= 1 && mes <= 12 && dia >= 1 && dia <= 31)) return null;
    const fecha = new Date(Date.UTC(anio, mes - 1, dia));
    if (fecha.getUTCMonth() !== mes - 1) return null; // 31/02 y similares
    return `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
};

// Fecha-dia como "YYYY-MM-DD" desde lo que traiga el formulario o el payload:
// moment, Date, ISO o el propio "YYYY-MM-DD".
export const textoFechaDia = (valor) => {
    if (vacio(valor)) return null;
    if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}/.test(valor)) {
        // Un ISO anclado al mediodia UTC conserva el dia en los primeros 10 caracteres.
        return valor.slice(0, 10);
    }
    const m = moment(valor);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

const serialDesdeTextoFecha = (texto) => {
    if (!texto) return null;
    const [anio, mes, dia] = texto.split("-").map(Number);
    return (Date.UTC(anio, mes - 1, dia) - EPOCA_EXCEL) / MS_DIA;
};

const celdaFecha = (valor, estilo) => {
    const serial = serialDesdeTextoFecha(textoFechaDia(valor));
    return serial === null ? celdaTexto("", estilo) : { v: serial, t: "n", s: estilo };
};

// Fecha de una celda del Excel del cliente: numero de serie (lo normal) o texto
// "15/08/2026", "15-8-26", "2026-08-15". Devuelve "YYYY-MM-DD" o null.
export const fechaDesdeCelda = (valor) => {
    if (vacio(valor)) return null;
    if (typeof valor === "number") {
        if (!(valor > 0 && valor < 2958466)) return null;
        const fecha = new Date(EPOCA_EXCEL + Math.floor(valor) * MS_DIA);
        return textoFechaValido(fecha.getUTCFullYear(), fecha.getUTCMonth() + 1, fecha.getUTCDate());
    }
    const texto = String(valor).trim();
    let m = texto.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (m) return textoFechaValido(Number(m[1]), Number(m[2]), Number(m[3]));
    m = texto.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
    if (m) {
        let anio = Number(m[3]);
        if (anio < 100) anio += 2000;
        return textoFechaValido(anio, Number(m[2]), Number(m[1]));
    }
    return null;
};

// Fecha-dia para el payload: anclada a las 12:00 UTC para que no se corra de dia.
export const fechaDiaParaGuardar = (valor) => {
    const texto = textoFechaDia(valor);
    return texto ? `${texto}T12:00:00.000Z` : undefined;
};

// ------------------------------------------------------------------ textos

export const normalizarTexto = (valor) =>
    String(valor ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();

// Texto de una celda: numeros sin notacion cientifica, saltos de linea colapsados.
const textoCelda = (valor) => {
    if (vacio(valor)) return "";
    if (typeof valor === "number") return String(valor);
    return String(valor).replace(/\s+/g, " ").trim();
};

// "XXX" es el relleno que deja el cliente en los campos que no sabe.
const esRelleno = (texto) => /^x+$/i.test(texto);
const valorCampo = (valor) => {
    const texto = textoCelda(valor);
    return esRelleno(texto) ? "" : texto;
};

// El canal con la escritura de la lista aunque lo hayan tecleado distinto.
const canalDeLista = (texto) =>
    CANALES_REQUERIMIENTO.find((canal) => normalizarTexto(canal) === normalizarTexto(texto)) || texto;

// Mismo criterio que el backend (Utils/formatoRequerimiento.utils.js).
export const textoIsbn = (valor) => {
    if (vacio(valor)) return "";
    if (typeof valor === "number") {
        if (!Number.isFinite(valor)) return "";
        return String(Number.isInteger(valor) ? valor : Math.round(valor));
    }
    return String(valor).trim();
};
export const normalizarIsbn = (valor) => textoIsbn(valor).replace(/[\s-]+/g, "").toUpperCase();

// ------------------------------------------------------------------ generar

// datos = null genera el formato vacio, igual al del cliente. Con datos es el que se
// regresa al guardar: lleva folio y fecha de la cotizacion y las columnas de precio.
// { fecha, folio, clienteInstitucion, canal, contacto, telefono, correo,
//   fechaRequerimiento, fechaEntregaRequerida,
//   destinos: [{ numero, direccion, contacto, telefono }],
//   productos: [{ isbn, descripcion, edicion, cantidad, destino, precioLista,
//                 descuento /* fraccion: 0.3 = 30% */ }] }
export const construirHojaFormatoRequerimiento = (datos) => {
    const d = datos || {};
    const conPrecios = !!datos;
    const destinos = d.destinos || [];
    const productos = d.productos || [];
    const anchos = conPrecios ? ANCHOS_CON_PRECIOS : ANCHOS_FORMATO;
    const ultimaColumna = anchos.length - 1;

    const hoja = {};
    const merges = [];
    const filas = [];
    const poner = (r, c, celda) => {
        hoja[XLSXStyle.utils.encode_cell({ r, c })] = celda;
    };
    // Toda celda dentro de un rango combinado lleva el mismo estilo, o el relleno y
    // los bordes se cortan en la primera.
    const combinar = (r1, c1, r2, c2, celda) => {
        for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++) {
                poner(r, c, r === r1 && c === c1 ? celda : { v: "", t: "s", s: celda.s });
            }
        }
        if (r1 !== r2 || c1 !== c2) merges.push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
    };
    const alto = (r, hpt) => {
        if (hpt && !(filas[r]?.hpt >= hpt)) filas[r] = { hpt };
    };
    const banda = (r, texto) => combinar(r, 0, r, ultimaColumna, celdaTexto(texto, estiloBanda));
    // Etiqueta en A y valor en B
    const filaDato = (r, etiqueta, celdaValor) => {
        poner(r, 0, celdaTexto(etiqueta, estiloEtiqueta));
        poner(r, 1, celdaValor);
    };

    // Titulo (fila 1)
    combinar(0, 0, 0, ultimaColumna, celdaTexto("FORMATO ÚNICO DE REQUERIMIENTO DE PEDIDO", estiloTitulo));
    alto(0, 21);

    // Datos del cliente (filas 3-8). La lista desplegable de Canal se agrega al
    // escribir el archivo (ver agregarListaCanal).
    banda(2, "Datos del Cliente");
    filaDato(3, "Cliente / Institución", celdaTexto(d.clienteInstitucion, estiloCelda));
    filaDato(4, "Canal", celdaTexto(d.canal, estiloCanal));
    filaDato(5, "Contacto", celdaTexto(d.contacto, estiloCelda));
    filaDato(6, "Teléfono", celdaTexto(d.telefono, estiloCelda));
    filaDato(7, "Correo", celdaTexto(d.correo, estiloCelda));
    if (conPrecios) {
        // Datos de la cotizacion: el formato del cliente no tiene donde ponerlos.
        poner(3, 3, celdaTexto("Folio", estiloEtiqueta));
        poner(3, 4, celdaTexto(d.folio, estiloCelda));
        poner(4, 3, celdaTexto("Fecha", estiloEtiqueta));
        poner(4, 4, celdaFecha(d.fecha, estiloFecha));
    }

    // Fechas (filas 10-12)
    banda(9, "Fechas");
    filaDato(10, "Fecha del requerimiento", celdaFecha(d.fechaRequerimiento, estiloFechaCliente));
    filaDato(11, "Fecha requerida de entrega", celdaFecha(d.fechaEntregaRequerida, estiloFechaCliente));

    // Direcciones de entrega (desde la fila 14)
    const filaBandaDestinos = 13;
    banda(filaBandaDestinos, "Direcciones de Entrega");
    ["Destino", "Dirección", "Contacto", "Teléfono"].forEach((titulo, c) =>
        poner(filaBandaDestinos + 1, c, celdaTexto(titulo, estiloEncabezado))
    );
    alto(filaBandaDestinos + 1, 15.6);

    const totalDestinos = Math.max(RENGLONES_DESTINO_MINIMOS, destinos.length);
    for (let i = 0; i < totalDestinos; i++) {
        const r = filaBandaDestinos + 2 + i;
        const destino = destinos[i];
        // El formato vacio trae los destinos ya numerados.
        const numero = destino ? destino.numero : datos ? null : i + 1;
        poner(r, 0, vacio(numero) ? celdaTexto("", estiloEntero) : celdaNumero(numero, estiloEntero));
        poner(r, 1, celdaTexto(destino?.direccion, estiloCeldaLarga));
        poner(r, 2, celdaTexto(destino?.contacto, estiloCelda));
        poner(r, 3, celdaTexto(destino?.telefono, estiloCelda));
        alto(r, altoTextoAjustado(destino?.direccion, ANCHO_DESCRIPCION));
    }

    // Detalle de productos: una fila en blanco despues de los destinos
    const filaBandaProductos = filaBandaDestinos + 2 + totalDestinos + 1;
    const filaEncabezadoProductos = filaBandaProductos + 1;
    banda(filaBandaProductos, "Detalle de Productos");
    const encabezados = ["ISBN", "Descripción", "Edición", "Cantidad", "Destino"];
    if (conPrecios) encabezados.push("Precio de lista", "Descuento", "Precio con descuento", "Total");
    encabezados.forEach((titulo, c) => poner(filaEncabezadoProductos, c, celdaTexto(titulo, estiloEncabezado)));
    alto(filaEncabezadoProductos, 15.6);

    const totalProductos = Math.max(RENGLONES_PRODUCTO_MINIMOS, productos.length);
    const primeraFilaProducto = filaEncabezadoProductos + 1;
    let sumaTotal = 0;
    for (let i = 0; i < totalProductos; i++) {
        const r = primeraFilaProducto + i;
        const p = productos[i] || {};

        poner(r, 0, { ...celdaTexto(textoIsbn(p.isbn), estiloIsbn), z: "@" });
        poner(r, 1, celdaTexto(p.descripcion, estiloCeldaLarga));
        poner(r, 2, celdaEdicion(p.edicion));
        poner(r, 4, vacio(p.destino) ? celdaTexto("", estiloEntero) : celdaNumero(p.destino, estiloEntero));
        alto(r, altoTextoAjustado(p.descripcion, ANCHO_DESCRIPCION));

        if (!conPrecios) {
            poner(r, 3, celdaTexto("", estiloEntero));
            continue;
        }
        const fx = r + 1; // fila en notacion de Excel
        const cantidad = Number(p.cantidad) || 0;
        const precio = Number(p.precioLista) || 0;
        const descuento = Number(p.descuento) || 0;
        const conDescuento = precio - descuento * precio;
        const total = cantidad * conDescuento;
        sumaTotal += total;

        poner(r, 3, celdaNumero(p.cantidad, estiloEntero));
        poner(r, 5, celdaNumero(p.precioLista, estiloMoneda));
        poner(r, 6, celdaNumero(p.descuento, estiloPorcentaje));
        poner(r, 7, celdaFormula(`F${fx}-(G${fx}*F${fx})`, conDescuento, estiloMoneda));
        poner(r, 8, celdaFormula(`D${fx}*H${fx}`, total, estiloMoneda));
    }

    let ultimaFila = primeraFilaProducto + totalProductos - 1;
    if (conPrecios) {
        const filaTotal = ultimaFila + 1; // en notacion de Excel es la ultima fila de producto
        poner(filaTotal, 7, celdaTexto("TOTAL", estiloTotalEtiqueta));
        poner(
            filaTotal,
            8,
            celdaFormula(`SUM(I${primeraFilaProducto + 1}:I${filaTotal})`, sumaTotal, estiloTotal)
        );
        ultimaFila = filaTotal;
    }

    hoja["!ref"] = XLSXStyle.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: ultimaFila, c: ultimaColumna } });
    hoja["!merges"] = merges;
    // Menos el 0.83 que suma la libreria al escribir.
    hoja["!cols"] = anchos.map((ancho) => ({ wch: ancho - 0.83 }));
    hoja["!rows"] = filas;
    return hoja;
};

export const construirLibroFormatoRequerimiento = (datos) => {
    const libro = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(libro, construirHojaFormatoRequerimiento(datos), "Requerimiento");
    return libro;
};

// xlsx-js-style no escribe validaciones de datos: la lista de Canal se mete en el XML
// de la hoja. Va despues de <mergeCells> y antes de <ignoredErrors>, como pide el
// orden del esquema de OOXML.
export const agregarListaCanal = (xml) => {
    const lista = `<dataValidations count="1"><dataValidation type="list" allowBlank="1" showErrorMessage="1" sqref="${CELDA_CANAL}"><formula1>"${CANALES_REQUERIMIENTO.join(",")}"</formula1></dataValidation></dataValidations>`;
    if (xml.includes("</mergeCells>")) return xml.replace("</mergeCells>", `</mergeCells>${lista}`);
    return xml.replace("</sheetData>", `</sheetData>${lista}`);
};

const archivoConListaCanal = async (libro) => {
    const zip = await JSZip.loadAsync(XLSXStyle.write(libro, { bookType: "xlsx", type: "array" }));
    const ruta = "xl/worksheets/sheet1.xml";
    const xml = await zip.file(ruta).async("string");
    zip.file(ruta, agregarListaCanal(xml), { createFolders: false });
    return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
};

export const generarArchivoFormatoRequerimiento = (datos) =>
    archivoConListaCanal(construirLibroFormatoRequerimiento(datos));

// El libro se arma antes de la parte asincrona para que un error con los datos le llegue
// al try/catch de quien llama. Si falla meter la lista, se baja el archivo sin ella.
export const descargarFormatoRequerimiento = (datos, nombreArchivo = "Formato_Unico_de_Requerimiento.xlsx") => {
    const libro = construirLibroFormatoRequerimiento(datos);
    return archivoConListaCanal(libro)
        .then((bytes) =>
            saveAs(
                new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
                nombreArchivo
            )
        )
        .catch((error) => {
            console.error("No se pudo agregar la lista de Canal al formato de requerimiento:", error);
            XLSXStyle.writeFile(libro, nombreArchivo);
        });
};

// Lo que se descarga al guardar la cotizacion. dataSat es el de la respuesta de
// agregarCotizacion: sus Conceptos salen de un Promise.all sobre req.body.insumos, asi
// que el i-esimo concepto es el i-esimo renglon del formulario.
export const datosFormatoDesdeCotizacion = ({ values, dataSat }) => {
    // Mismo limpiado que se guarda: textos recortados y sin destinos vacios.
    const formato = formatoRequerimientoParaGuardar(values?.formatoRequerimiento) || {};
    const conceptos = dataSat?.Conceptos || [];
    const redondear = (n) => Math.round(n * 1e6) / 1e6;
    return {
        fecha: values?.fechaCotizacion || new Date(),
        folio: `${dataSat?.Serie || ""}${dataSat?.Folio ?? ""}`,
        clienteInstitucion: formato.clienteInstitucion || dataSat?.Receptor?.Nombre || "",
        canal: formato.canal,
        contacto: formato.contacto,
        telefono: formato.telefono,
        correo: formato.correo,
        fechaRequerimiento: formato.fechaRequerimiento,
        fechaEntregaRequerida: formato.fechaEntregaRequerida,
        destinos: formato.destinos || [],
        productos: conceptos.map((concepto, i) => {
            const importe = Number(concepto.Importe) || 0;
            const descuento = Number(concepto.Descuento) || 0;
            const renglon = values?.insumos?.[i];
            const destino = renglon?.destinoRequerimiento;
            return {
                isbn: concepto.CodigoBarras || concepto.NoIdentificacion || "",
                descripcion: concepto.Descripcion,
                edicion: renglon?.edicionRequerimiento || "",
                cantidad: Number(concepto.Cantidad) || 0,
                destino: vacio(destino) ? null : destino,
                precioLista: redondear(Number(concepto.ValorUnitario) || 0),
                descuento: importe > 0 ? redondear(descuento / importe) : 0,
            };
        }),
    };
};

// Hay datos del formato que valga la pena regresar al cliente.
export const hayDatosFormatoRequerimiento = (values) => {
    const f = values?.formatoRequerimiento;
    const enEncabezado =
        !!f &&
        [
            "clienteInstitucion",
            "canal",
            "contacto",
            "telefono",
            "correo",
            "fechaRequerimiento",
            "fechaEntregaRequerida",
        ].some((campo) => !vacio(f[campo]));
    const conDestinos = !!f && (f.destinos || []).some(Boolean);
    const conDatosPorRenglon = (values?.insumos || []).some(
        (i) => !vacio(i?.destinoRequerimiento) || !vacio(i?.edicionRequerimiento)
    );
    return enEncabezado || conDestinos || conDatosPorRenglon;
};

// Normaliza lo que capturo la pestana Requerimiento para mandarlo al backend:
// textos recortados, fechas-dia ancladas y destinos sin filas vacias. Devuelve
// undefined si no hay nada que guardar.
export const formatoRequerimientoParaGuardar = (formato) => {
    if (!formato) return undefined;
    const texto = (v) => (vacio(v) ? undefined : String(v).trim());
    const destinos = (formato.destinos || [])
        .filter(Boolean)
        .map((d) => ({
            numero: vacio(d.numero) || Number.isNaN(Number(d.numero)) ? undefined : Number(d.numero),
            direccion: texto(d.direccion),
            contacto: texto(d.contacto),
            telefono: texto(d.telefono),
        }))
        .filter((d) => Object.values(d).some((v) => v !== undefined));
    const resultado = {
        clienteInstitucion: texto(formato.clienteInstitucion),
        canal: texto(formato.canal),
        contacto: texto(formato.contacto),
        telefono: texto(formato.telefono),
        correo: texto(formato.correo),
        fechaRequerimiento: fechaDiaParaGuardar(formato.fechaRequerimiento),
        fechaEntregaRequerida: fechaDiaParaGuardar(formato.fechaEntregaRequerida),
        destinos,
    };
    const hayAlgo = Object.entries(resultado).some(([k, v]) => (k === "destinos" ? v.length > 0 : v !== undefined));
    return hayAlgo ? resultado : undefined;
};

// ------------------------------------------------------------------ leer

const indiceEncabezado = (encabezados, prueba) => encabezados.findIndex((e) => e && prueba(e));

const numeroDestino = (valor) => {
    if (vacio(valor)) return null;
    const n = Number(String(valor).trim());
    return Number.isFinite(n) ? n : NaN;
};

const descuentoPorcentaje = (valor) => {
    if (vacio(valor)) return 0;
    let n = typeof valor === "number" ? valor : Number(String(valor).replace("%", "").trim());
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (typeof valor === "number" && n < 1) n *= 100; // celda con formato 0%: 0.3
    if (n > 100) return 0;
    return Math.round(n * 10000) / 10000;
};

const esFilaEjemplo = (fila) => (fila || []).some((celda) => normalizarTexto(celda) === "EJEMPLO");

const esEncabezadoDestinos = (texto) =>
    texto === "DESTINO" || texto.startsWith("NO. DE DESTINO") || texto.startsWith("NO DE DESTINO");

// Lee el formato del cliente. `filas` es la hoja como arreglo de arreglos
// (sheet_to_json con header: 1). Las secciones se ubican por su texto y no por numero
// de fila: el cliente inserta renglones (otro destino, otro producto) y todo lo de
// abajo se recorre.
export const leerFormatoRequerimiento = (filas) => {
    const avisos = [];
    const filasSeguras = (filas || []).map((f) => (Array.isArray(f) ? f : []));
    const columnaA = filasSeguras.map((f) => normalizarTexto(f[0]));

    const filaIsbn = columnaA.findIndex((t) => t === "ISBN");
    if (filaIsbn === -1) {
        return { esFormato: false, encabezado: {}, destinos: [], productos: [], avisos };
    }
    const filaDirecciones = columnaA.findIndex((t) => t.startsWith("DIRECCIONES DE ENTREGA"));
    const filaEncabezadoDestinos = columnaA.findIndex(
        (t, i) => i > filaDirecciones && i < filaIsbn && esEncabezadoDestinos(t)
    );
    const filaBandaProductos = columnaA.findIndex((t) => t.startsWith("DETALLE DE PRODUCTOS"));
    const limiteEncabezado = [filaDirecciones, filaEncabezadoDestinos, filaIsbn].find((i) => i > -1);

    // --- Datos del cliente y fechas: etiqueta en A y valor en B. Solo B: en el formato
    // que regresa Biwo, a la derecha estan el folio y la fecha de la cotizacion.
    const valorDeEtiqueta = (prueba) => {
        const i = columnaA.findIndex((t, idx) => idx < limiteEncabezado && t && prueba(t));
        return i === -1 ? "" : filasSeguras[i][1];
    };
    const fechaDeEtiqueta = (prueba, nombre) => {
        const valor = valorDeEtiqueta(prueba);
        if (vacio(valor) || esRelleno(textoCelda(valor))) return null;
        const fecha = fechaDesdeCelda(valor);
        if (!fecha) avisos.push(`No se entendió la ${nombre} "${textoCelda(valor)}".`);
        return fecha;
    };
    const canal = valorCampo(valorDeEtiqueta((t) => t === "CANAL"));
    const encabezado = {
        clienteInstitucion: valorCampo(valorDeEtiqueta((t) => t.startsWith("CLIENTE"))),
        canal: canal ? canalDeLista(canal) : "",
        contacto: valorCampo(valorDeEtiqueta((t) => t === "CONTACTO")),
        telefono: valorCampo(valorDeEtiqueta((t) => t.startsWith("TELEFONO"))),
        correo: valorCampo(valorDeEtiqueta((t) => t.startsWith("CORREO"))),
        fechaRequerimiento: fechaDeEtiqueta((t) => t.startsWith("FECHA DEL REQUERIMIENTO"), "fecha del requerimiento"),
        fechaEntregaRequerida: fechaDeEtiqueta(
            (t) => t.startsWith("FECHA REQUERIDA DE ENTREGA"),
            "fecha requerida de entrega"
        ),
    };

    // --- Direcciones de entrega
    const destinos = [];
    if (filaEncabezadoDestinos > -1) {
        const enc = filasSeguras[filaEncabezadoDestinos].map(normalizarTexto);
        const columnas = {
            direccion: indiceEncabezado(enc, (e) => e.includes("DIRECCION")),
            contacto: indiceEncabezado(enc, (e) => e.includes("CONTACTO")),
            telefono: indiceEncabezado(enc, (e) => e.includes("TELEFONO")),
        };
        const filaFin = Math.min(...[filaBandaProductos, filaIsbn].filter((i) => i > filaEncabezadoDestinos));
        for (let i = filaEncabezadoDestinos + 1; i < filaFin; i++) {
            const fila = filasSeguras[i];
            const destino = {};
            Object.entries(columnas).forEach(([campo, c]) => {
                destino[campo] = c > -1 ? valorCampo(fila[c]) : "";
            });
            const conDatos = Object.values(destino).some((v) => v !== "");
            if (!conDatos) continue;
            if (esFilaEjemplo(fila)) {
                avisos.push(`Se omitió la dirección de ejemplo de la fila ${i + 1}.`);
                continue;
            }
            const numero = numeroDestino(fila[0]);
            if (numero === null || Number.isNaN(numero)) {
                avisos.push(`La dirección de la fila ${i + 1} no tiene número de destino.`);
            }
            destinos.push({ numero: Number.isNaN(numero) ? null : numero, ...destino });
        }
        const vistos = new Set();
        destinos.forEach((d) => {
            if (d.numero === null) return;
            if (vistos.has(d.numero)) avisos.push(`El destino ${d.numero} aparece más de una vez.`);
            vistos.add(d.numero);
        });
    }
    const numerosDestino = new Set(destinos.map((d) => d.numero).filter((n) => n !== null));

    // --- Detalle de productos
    const enc = filasSeguras[filaIsbn].map(normalizarTexto);
    const col = {
        isbn: 0,
        descripcion: indiceEncabezado(enc, (e) => e.includes("DESCRIPCION") || e.includes("TITULO")),
        edicion: indiceEncabezado(enc, (e) => e.includes("EDICION")),
        cantidad: indiceEncabezado(enc, (e) => e.includes("CANTIDAD")),
        destino: indiceEncabezado(enc, (e) => e.includes("DESTINO")),
        // Solo viene en el formato que regresa Biwo: si lo vuelven a subir, se conserva.
        descuento: indiceEncabezado(enc, (e) => e.startsWith("DESCUENTO")),
    };
    const celda = (fila, c) => (c > -1 ? fila[c] : "");

    const productos = [];
    for (let i = filaIsbn + 1; i < filasSeguras.length; i++) {
        const fila = filasSeguras[i];
        const isbn = textoIsbn(fila[col.isbn]);
        const descripcion = textoCelda(celda(fila, col.descripcion));
        if (!isbn && !descripcion) continue;
        if (esFilaEjemplo(fila)) {
            avisos.push(`Se omitió el producto de ejemplo de la fila ${i + 1}.`);
            continue;
        }
        const cantidadCruda = celda(fila, col.cantidad);
        const cantidad = vacio(cantidadCruda) ? 0 : Number(String(cantidadCruda).replace(/,/g, "").trim());
        const destino = numeroDestino(celda(fila, col.destino));

        let error = null;
        if (!isbn) error = "Sin ISBN";
        else if (!Number.isFinite(cantidad) || cantidad <= 0) error = "Cantidad inválida";

        if (!error && Number.isNaN(destino)) {
            avisos.push(`Fila ${i + 1}: el destino "${textoCelda(celda(fila, col.destino))}" no es un número.`);
        } else if (!error && destino !== null && !numerosDestino.has(destino)) {
            avisos.push(`Fila ${i + 1}: el destino ${destino} no está en las direcciones de entrega.`);
        }

        productos.push({
            fila: i + 1,
            isbn,
            descripcion,
            edicion: valorCampo(celda(fila, col.edicion)),
            cantidad: Number.isFinite(cantidad) ? cantidad : 0,
            destino: destino === null || Number.isNaN(destino) ? null : destino,
            descuento: descuentoPorcentaje(celda(fila, col.descuento)),
            error,
        });
    }

    return { esFormato: true, encabezado, destinos, productos, avisos };
};
